import { useState, useEffect, useRef, useCallback } from 'react';

// base WebSocket URL (include the /ws path if required by server)
const WS_URL =
  process.env.REACT_APP_WS_URL ||
  "wss://portfolio-backend-production-1286.up.railway.app/ws";

export function useWebSocket() {
  const [sessionId, setSessionId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const messageQueueRef = useRef([]);
  const responseCallbacksRef = useRef([]);

  // Register callback for responses
  const onResponse = useCallback((callback) => {
    responseCallbacksRef.current.push(callback);
    // Return unsubscribe function
    return () => {
      responseCallbacksRef.current = responseCallbacksRef.current.filter(cb => cb !== callback);
    };
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    setIsConnecting(true);
    setError(null);

    try {
      // the configured WS_URL should already point to the websocket endpoint
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        setIsConnecting(false);
        setError(null);
        
        // Process any queued messages
        while (messageQueueRef.current.length > 0) {
          const message = messageQueueRef.current.shift();
          ws.send(JSON.stringify(message));
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket message received:', data);

          // Handle initial handshake - session_id response
          if (data.session_id && !data.response) {
            setSessionId(data.session_id);
            console.log('Session ID received:', data.session_id);
          }
          
          // Handle chat response - call all registered callbacks
          if (data.response) {
            console.log('Response received, calling callbacks. Response:', data.response);
            responseCallbacksRef.current.forEach(callback => {
              callback({
                response: data.response,
                confidence: data.confidence,
                sessionId: data.session_id
              });
            });
          }
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      ws.onerror = (event) => {
        console.error('WebSocket error:', event);
        setError({ type: 'error', message: 'Connection error' });
        setIsConnecting(false);
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        setIsConnecting(false);

        // If this was a clean/intentional close (1000), clear any error
        if (event.code === 1000) {
          setError(null);
        } else {
          // mark as a disconnect (transient/unclean) so UI can decide how to notify
          setError({ type: 'disconnect', code: event.code, message: 'Connection closed' });

          // Attempt to reconnect after 3 seconds
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('Attempting to reconnect...');
            connect();
          }, 3000);
        }
      };
    } catch (err) {
      console.error('Failed to create WebSocket connection:', err);
      setError({ type: 'error', message: 'Failed to connect' });
      setIsConnecting(false);
    }
  }, []);

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'Intentional disconnect');
      wsRef.current = null;
    }
    
    setIsConnected(false);
    setSessionId(null);
  }, []);

  // Send a chat message
  const sendMessage = useCallback((text) => {
    if (!text?.trim()) {
      return;
    }

    const message = {
      text: text.trim(),
      session_id: sessionId
    };

    console.log('Sending message:', message);

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else if (wsRef.current?.readyState === WebSocket.CONNECTING) {
      // Queue message to be sent when connection opens
      messageQueueRef.current.push(message);
    } else {
      // Not connected, reconnect and queue
      messageQueueRef.current.push(message);
      connect();
    }
  }, [sessionId, connect]);

  // Clear session (start new chat)
  const clearSession = useCallback(() => {
    setSessionId(null);
    setError(null);
    
    // Reconnect to get a new session
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.close(1000, 'New session requested');
    }
    connect();
  }, [connect]);

  // Auto-connect on mount
  useEffect(() => {
    connect();
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
    };
  }, [connect]);

  return {
    sessionId,
    isConnected,
    isConnecting,
    error,
    sendMessage,
    clearSession,
    connect,
    disconnect,
    onResponse
  };
}